<template>
  <div class="waw-ack">
    <p v-if="readOnly" class="waw-locked">
      These confirmations are saved and can&rsquo;t be changed here. If anything needs to change,
      contact Teamio and we&rsquo;ll sort it out with you.
    </p>

    <p v-if="!readOnly" class="waw-ack__lead">
      Moving a number you already use changes a few things for good. Please confirm your practice
      understands each one &mdash; every box has to be ticked before we can set it up.
    </p>

    <!-- Defensive: the acknowledgement wording is served by my-status, never hardcoded here. If it
         did not arrive there is nothing to tick, so we say so plainly and leave Continue disabled
         (the shell's `byoAckComplete` is false on an empty key set) rather than showing a blank
         screen a practice could sail through without acknowledging anything. -->
    <div v-if="!items.length" class="waw-ack__error" data-test="waw-byo_ack-error">
      We couldn&rsquo;t load the details you need to confirm just now, so there is nothing to tick
      yet. Please refresh this page, and contact Teamio if it keeps happening &mdash; you won&rsquo;t
      be able to continue until these load.
    </div>

    <ul v-else class="waw-ack__list">
      <li v-for="item in items" :key="item.key" class="waw-ack__item">
        <label class="waw-ack__row">
          <!-- One static data-test on the checkbox (the wizard's data-test contract reads source, so
               a v-for cannot give each rendered box its own literal id); the per-item key rides on
               `data-ack-key` so a driver can still address a specific box at runtime. -->
          <input
            type="checkbox"
            class="waw-ack__box"
            data-test="waw-byo_ack-item"
            :data-ack-key="item.key"
            :checked="isChecked(item.key)"
            :disabled="readOnly"
            @change="toggle(item.key, $event.target.checked)"
          >
          <span class="waw-ack__text">{{ item.text }}</span>
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * Wizard screen (bring-your-own path only) — the practice acknowledges every consequence of moving a
 * WhatsApp number it already uses, before the one my-triage POST that stores its answers.
 *
 * ── THE WORDING IS THE SERVER'S, NEVER OURS ──────────────────────────────────────────────────────
 * Each `item.text` is rendered EXACTLY as `my-status` returns it (`byoAckWording.items`, sourced from
 * conversations-service/lib/waOnboarding/byoAck.js and pinned to `byoAckWording.version`). This is a
 * legal artifact: a practice who ticks these has provably agreed to THESE words at THIS version. A
 * copy pasted into this file would be a second source of truth that drifts from the version the
 * backend records, so the practice would attest to sentences other than the ones stored. There is no
 * acknowledgement sentence hardcoded anywhere in this component — do not add one.
 *
 * ── HOW THE ANSWERS TRAVEL ───────────────────────────────────────────────────────────────────────
 * The step owns no state. It reflects the shell's draft (`acknowledgements`, keyed by `item.key`) and
 * emits the whole updated object back through the shared `update` event on every toggle. The shell
 * spreads those keys into the my-triage answers on the BYO path — the backend's
 * `normalizeTriageAnswers` reads `answers[key]` for each of its `BYO_ACK_KEYS`. No POST of its own
 * (the shell owns axios); no new backend field beyond the keys the server already expects.
 *
 * ── READ-ONLY AFTER SUBMISSION ───────────────────────────────────────────────────────────────────
 * my-triage accepts one POST per site, so coming back here after it is a REVIEW: the boxes render
 * ticked from the echoed answers and are disabled, exactly like the triage and confirm screens.
 */
export default {
  name: 'WizardByoAckStep',

  props: {
    /** The acknowledgement wording served by my-status: `[{ key, text }]`. Empty ⇒ nothing to tick. */
    items: { type: Array, default: () => [] },
    /** The shell's draft acknowledgements, keyed by `item.key` → true. */
    acknowledgements: { type: Object, default: () => ({}) },
    /** True once the answers are saved server-side — from then on this screen is a review. */
    readOnly: { type: Boolean, default: false },
  },

  methods: {
    isChecked(key) {
      return !!(this.acknowledgements && this.acknowledgements[key] === true);
    },

    toggle(key, checked) {
      if (this.readOnly) return;
      // Emit the FULL updated map, never a single key: the shell replaces `draft.acknowledgements`
      // with what we send, so a partial patch would drop the other boxes. Unticking removes the key
      // (rather than storing `false`) so the submitted set contains only boxes that are actually on.
      const next = { ...this.acknowledgements };
      if (checked === true) next[key] = true;
      else delete next[key];
      this.$emit('update', { acknowledgements: next });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-locked {
  margin: 0 0 1.2rem;
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  color: $pp-text-muted;
  font-size: 0.83rem;
  line-height: 1.6;
}

.waw-ack__lead {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  line-height: 1.6;
  color: $pp-text-strong;
  max-width: 640px;
}

.waw-ack__error {
  margin: 0;
  padding: 0.75rem 0.85rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-red;
  color: $pp-red-text;
  font-size: 0.85rem;
  line-height: 1.6;
  max-width: 640px;
}

.waw-ack__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-width: 640px;
}

.waw-ack__item {
  margin: 0;
}

.waw-ack__row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0;
  padding: 0.75rem 0.85rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  cursor: pointer;
}

.waw-ack__box {
  margin-top: 0.15rem;
  flex: 0 0 auto;
}

.waw-ack__text {
  font-size: 0.85rem;
  line-height: 1.6;
  color: $pp-text-strong;
}
</style>
